import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { exhaustMap, catchError, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

import { OwnerService } from '../owner.service';
import { OwnerActions } from '../actions';
import { StorefrontService } from '../storefront.service';
import { EnvironmentService } from '../environment.service';
import { DestinationSelectSetting, DestinationSelectSettingUtil } from '../../../objects/destination-select-setting';



@Injectable()
export class OwnerEffects {
  private actions$ = inject(Actions);
  private ownerService = inject(OwnerService);
  private storefrontService = inject(StorefrontService);
  private envirnmentService = inject(EnvironmentService);

  init$ = createEffect(() => this.actions$
    .pipe(
      ofType(ROOT_EFFECTS_INIT),
      exhaustMap(() => this.storefrontService
        .getStorefrontAndServiceAreas()
        .pipe(
          switchMap(({storefront, serviceAreas}) => [
            OwnerActions.loadStorefrontSuccess({ storefront }),
            OwnerActions.loadServiceAreasSuccess({ serviceAreas }),
            OwnerActions.setDestinationSelectSetting({
              destinationSelectSetting: DestinationSelectSettingUtil.getDestinationSelectSetting({
                destinationSearch: storefront.destination_search,
                delivery: storefront.delivery
              })
            }),
          ]),
          catchError(error => {
            if (!this.storefrontFallbackToOwner) {
              return of(
                OwnerActions.loadStorefrontFailure({ error }),
                OwnerActions.loadServiceAreasFailure({ error }),
                OwnerActions.setDestinationSelectSetting({ destinationSelectSetting: DestinationSelectSetting.none }),
              );
            }

            /**
             * If we're in fallback to owner case, let's try to load owner isntead
             * So called "Legacy mode" where everything is set up based on owner groups
             * incl. images, tipping enabled and service areas.
             *
             * Also in such case we have to follow a different flow when getting
             * destinations and products
             */
            return this.ownerService
              .getOwnerAndServiceAreas()
              .pipe(
                switchMap(({ owner, serviceAreas }) => [
                  OwnerActions.loadStorefrontFailure({ error }),
                  OwnerActions.loadOwnerSuccess({ owner }),
                  OwnerActions.loadServiceAreasSuccess({ serviceAreas }),
                  OwnerActions.setDestinationSelectSetting({ destinationSelectSetting: DestinationSelectSetting.both }),
                ]),
                catchError(ownerError => of(
                  OwnerActions.loadStorefrontFailure({ error }),
                  OwnerActions.loadOwnerFailure({ error: ownerError }),
                  OwnerActions.loadServiceAreasFailure({ error: ownerError }),
                  OwnerActions.setDestinationSelectSetting({ destinationSelectSetting: DestinationSelectSetting.none }),
                )),
              );
          }),
        ),
      ),
    ),
  );

  loadOwner$ = createEffect(() => this.actions$
    .pipe(
      ofType(OwnerActions.loadOwner),
      exhaustMap(() => this.ownerService
        .getOwner()
        .pipe(
          map(owner => OwnerActions.loadOwnerSuccess({ owner })),
          catchError(error => of(OwnerActions.loadOwnerFailure({ error }))),
        ),
      ),
    ),
  );

  loadStorefront$ = createEffect(() => this.actions$
    .pipe(
      ofType(OwnerActions.loadStorefronts),
      exhaustMap(() => this.storefrontService
        .get()
        .pipe(
          map(storefront => OwnerActions.loadStorefrontSuccess({ storefront })),
          catchError(error => of(OwnerActions.loadStorefrontFailure({ error }))),
        ),
      ),
    ),
  );

  private storefrontFallbackToOwner = false;

  constructor() {
    const { storefrontFallbackToOwner } = this.envirnmentService.resolve();

    this.storefrontFallbackToOwner = storefrontFallbackToOwner;
  }
}

